import { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../../config/constants';
import scanMyFitDemoData from '../../data/scanMyFitDemo.json';
import './ScanMyFitDrawer.css';

const DEMO_RESULT = scanMyFitDemoData;

const sanitizeResult = (apiData) => {
  const data = apiData?.data || apiData;
  return {
    fitScore: Number(data?.fitScore ?? DEMO_RESULT.fitScore),
    fitLabel: data?.fitLabel || DEMO_RESULT.fitLabel,
    strengths: Array.isArray(data?.strengths) ? data.strengths : DEMO_RESULT.strengths,
    gaps: Array.isArray(data?.gaps) ? data.gaps : DEMO_RESULT.gaps,
    actionRows: Array.isArray(data?.actionRows) ? data.actionRows : DEMO_RESULT.actionRows,
    weeklyPlan: Array.isArray(data?.weeklyPlan) ? data.weeklyPlan : DEMO_RESULT.weeklyPlan,
  };
};

function ScanMyFitDrawer({ isOpen, job, onClose, onApplySuccess }) {
  const [mode, setMode] = useState('choose'); // 'choose' | 'upload' | 'text' | 'result'
  const [skillsText, setSkillsText] = useState('');
  const [fileName, setFileName] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [applicantName, setApplicantName] = useState('');
  const [emailId, setEmailId] = useState('');
  const [skills, setSkills] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [apiNotice, setApiNotice] = useState('');
  const [applyNotice, setApplyNotice] = useState('');
  const [result, setResult] = useState(null);
  const drawerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset state when drawer opens for a new job
  useEffect(() => {
    if (isOpen) {
      setMode('choose');
      setSkillsText('');
      setFileName('');
      setResumeFile(null);
      setApplicantName('');
      setEmailId('');
      setSkills('');
      setResult(null);
      setApiNotice('');
      setApplyNotice('');
    }
  }, [isOpen, job?.id]);

  // Trap focus + close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setResumeFile(file);
      setFileName(file.name);
      setApplyNotice('');
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

  const downloadBlob = (content, contentType, filename) => {
    const blob = new Blob([content], { type: contentType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  const exportToExcel = () => {
    if (!result) return;
    const rows = [
      ['Fit Score', String(result.fitScore)],
      ['Fit Label', result.fitLabel],
      [],
      ['Track', 'Goal', 'Focus', 'Output'],
      ...result.actionRows.map((row) => [row.track, row.goal, row.focus, row.output]),
      [],
      ['Week', 'Learn', 'Practice', 'Share', 'Outcome'],
      ...result.weeklyPlan.map((row) => [row.week, row.learn, row.practice, row.share, row.outcome]),
    ];
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadBlob(csv, 'text/csv;charset=utf-8', `${job?.title || 'role'}-fit-plan.csv`);
  };

  const exportToPdf = () => {
    if (!result) return;
    const printable = window.open('', '_blank', 'width=900,height=900');
    if (!printable) return;
    printable.document.write(`
      <html>
        <head>
          <title>Fit Plan Export</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #1e2a39; }
            h1 { margin: 0 0 8px; }
            h2 { margin: 18px 0 8px; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0 16px; }
            th, td { border: 1px solid #cfd6dd; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f3f5f7; }
          </style>
        </head>
        <body>
          <h1>${job?.title || 'Role'} Fit Plan</h1>
          <p><strong>Company:</strong> ${job?.company || '-'}</p>
          <p><strong>Fit Score:</strong> ${result.fitScore}/100 (${result.fitLabel})</p>
          <h2>Learn / Practice / Share</h2>
          <table>
            <thead><tr><th>Track</th><th>Goal</th><th>Focus</th><th>Output</th></tr></thead>
            <tbody>
              ${result.actionRows.map((row) => `<tr><td>${row.track}</td><td>${row.goal}</td><td>${row.focus}</td><td>${row.output}</td></tr>`).join('')}
            </tbody>
          </table>
          <h2>Weekly Plan</h2>
          <table>
            <thead><tr><th>Week</th><th>Learn</th><th>Practice</th><th>Share</th><th>Outcome</th></tr></thead>
            <tbody>
              ${result.weeklyPlan.map((row) => `<tr><td>${row.week}</td><td>${row.learn}</td><td>${row.practice}</td><td>${row.share}</td><td>${row.outcome}</td></tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printable.document.close();
    printable.focus();
    printable.print();
  };

  const exportToImage = () => {
    if (!result) return;
    const summary = [
      `${job?.title || 'Role'} Fit Plan`,
      `${job?.company || ''}`,
      `Score: ${result.fitScore}/100 (${result.fitLabel})`,
      '',
      'Learn / Practice / Share',
      ...result.actionRows.map((row) => `${row.track}: ${row.focus}`),
      '',
      'Weekly Plan',
      ...result.weeklyPlan.map((row) => `${row.week}: ${row.outcome}`),
    ].join('\n');
    const escaped = summary
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="900"><rect width="100%" height="100%" fill="#f2f4f6"/><text x="40" y="64" style="font: 24px Arial; fill: #1e2a39; white-space: pre;">${escaped}</text></svg>`;
    downloadBlob(svg, 'image/svg+xml;charset=utf-8', `${job?.title || 'role'}-fit-plan.svg`);
  };

  const handleAnalyse = async () => {
    setLoading(true);
    setApiNotice('');

    try {
      if (mode === 'upload') {
        const applicationJobId = Number.parseInt(job?.applicationJobId ?? job?.id, 10);
        const roleName = String(job?.title || '').trim();
        const formData = new FormData();
        formData.append('jobid', String(applicationJobId));
        formData.append('rolename', roleName);
        formData.append('name', applicantName.trim());
        formData.append('emailid', emailId.trim());
        formData.append('skills', skills.trim());
        formData.append('resume', resumeFile);

        const response = await fetch(API_ENDPOINTS.JOBS_APPLY, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || `Apply API failed with status ${response.status}`);
        }

        setResult(null);
        setApiNotice('Application submitted successfully. Our team will review your profile and contact you soon.');
        if (typeof onApplySuccess === 'function') {
          onApplySuccess(data);
        }
      } else {
        // Text mode currently has no compatible backend endpoint.
        setResult(DEMO_RESULT);
        setApiNotice('Live fit API unavailable for text mode. Showing demo analysis data.');
      }

      setMode('result');
    } catch (error) {
      if (mode === 'upload') {
        setApiNotice(error.message || 'Unable to submit application right now. Please try again.');
      } else {
        setResult(DEMO_RESULT);
        setMode('result');
        setApiNotice(error.message || 'Live API unavailable. Showing demo analysis data.');
      }
    } finally {
      setLoading(false);
    }
  };

  const canApply = !!resumeFile && applicantName.trim() && isValidEmail(emailId) && skills.trim();
  const canAnalyse = mode === 'upload' ? canApply : skillsText.trim().length > 40;

  const handleApplyNow = async () => {
    if (!canApply || applyLoading) {
      return;
    }

    setApplyLoading(true);
    setApplyNotice('');

    try {
      const applicationJobId = Number.parseInt(job?.applicationJobId ?? job?.id, 10);
      const roleName = String(job?.title || '').trim();
      const formData = new FormData();
      formData.append('jobid', String(applicationJobId));
      formData.append('rolename', roleName);
      formData.append('name', applicantName.trim());
      formData.append('emailid', emailId.trim());
      formData.append('skills', skills.trim());
      formData.append('resume', resumeFile);

      const response = await fetch(API_ENDPOINTS.JOBS_APPLY, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || `Apply API failed with status ${response.status}`);
      }

      if (typeof onApplySuccess === 'function') {
        onApplySuccess(data);
      }
      onClose();
    } catch (error) {
      setApplyNotice(error.message || 'Unable to submit application right now. Please try again.');
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`smf-overlay ${isOpen ? 'smf-overlay--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        className={`smf-drawer ${isOpen ? 'smf-drawer--open' : ''} ${mode === 'result' ? 'smf-drawer--result' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Scan My Fit"
      >
        {/* Header */}
        <div className="smf-drawer__header">
          <div className="smf-drawer__header-meta">
            {job && (
              <>
                <div className="smf-drawer__job-avatar" aria-hidden="true">{job.initials}</div>
                <div>
                  <p className="smf-drawer__job-title">{job.title}</p>
                  <p className="smf-drawer__job-company">{job.company}</p>
                </div>
              </>
            )}
          </div>
          <button className="smf-drawer__close" onClick={onClose} aria-label="Close panel">✕</button>
        </div>

        <div className="smf-drawer__body">

          {/* ── CHOOSE MODE ── */}
          {mode === 'choose' && (
            <div className="smf-section smf-section--choose">
              <h2 className="smf-heading">Scan My Fit</h2>
              <p className="smf-subtext">
                Share your profile to get an instant fitment score and a personalised preparation plan for this role.
              </p>
              <div className="smf-choose-grid">
                <button
                  className="smf-choose-card"
                  onClick={() => setMode('upload')}
                >
                  <span className="smf-choose-card__icon">📄</span>
                  <strong>Upload Résumé</strong>
                  <span>PDF, DOC, or DOCX</span>
                </button>
                <button
                  className="smf-choose-card"
                  onClick={() => setMode('text')}
                >
                  <span className="smf-choose-card__icon">✏️</span>
                  <strong>Paste Skills</strong>
                  <span>Describe your experience</span>
                </button>
              </div>
            </div>
          )}

          {/* ── UPLOAD MODE ── */}
          {mode === 'upload' && (
            <div className="smf-section">
              <button className="smf-back" onClick={() => setMode('choose')}>← Back</button>
              <h2 className="smf-heading">Upload Your Résumé</h2>
              <p className="smf-subtext">We'll match it against the role's requirements and benchmark track.</p>

              <div className="smf-form-grid">
                <label className="smf-field">
                  <span className="smf-field__label">Full Name</span>
                  <input
                    className="smf-input"
                    type="text"
                    placeholder="Enter your name"
                    value={applicantName}
                    onChange={(e) => setApplicantName(e.target.value)}
                  />
                </label>

                <label className="smf-field">
                  <span className="smf-field__label">Email</span>
                  <input
                    className="smf-input"
                    type="email"
                    placeholder="you@example.com"
                    value={emailId}
                    onChange={(e) => setEmailId(e.target.value)}
                  />
                </label>
              </div>

              <label className="smf-field">
                <span className="smf-field__label">Skills</span>
                <input
                  className="smf-input"
                  type="text"
                  placeholder="Node.js, React, Azure"
                  value={skills}
                  onChange={(e) => setSkills(e.target.value)}
                />
              </label>

              <div
                className="smf-upload-zone"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                tabIndex={0}
                role="button"
                aria-label="Upload résumé file"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="smf-upload-zone__input"
                  onChange={handleFileChange}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                {fileName ? (
                  <p className="smf-upload-zone__name">📎 {fileName}</p>
                ) : (
                  <>
                    <span className="smf-upload-zone__icon">⬆</span>
                    <p>Click or drag to upload</p>
                    <p className="smf-upload-zone__hint">PDF · DOC · DOCX · max 5 MB</p>
                  </>
                )}
              </div>

              {!isValidEmail(emailId) && emailId.trim() ? (
                <p className="smf-char-hint smf-char-hint--error">Enter a valid email address</p>
              ) : null}

              <button
                className="smf-btn smf-btn--primary"
                disabled={!canAnalyse || loading}
                onClick={handleAnalyse}
              >
                {loading ? <span className="smf-spinner" /> : 'Analyse Fit →'}
              </button>
            </div>
          )}

          {/* ── TEXT MODE ── */}
          {mode === 'text' && (
            <div className="smf-section">
              <button className="smf-back" onClick={() => setMode('choose')}>← Back</button>
              <h2 className="smf-heading">Describe Your Skills</h2>
              <p className="smf-subtext">Paste your LinkedIn summary, key projects, or a short bio — anything that captures your experience.</p>

              <textarea
                className="smf-textarea"
                placeholder="e.g. 8 years in enterprise software, led Copilot Studio PoCs, certified AZ-900, managed cross-functional agile teams…"
                value={skillsText}
                onChange={(e) => setSkillsText(e.target.value)}
                rows={8}
                aria-label="Skills and experience"
              />
              <p className="smf-char-hint">{skillsText.length} chars · aim for 100+</p>

              <button
                className="smf-btn smf-btn--primary"
                disabled={!canAnalyse || loading}
                onClick={handleAnalyse}
              >
                {loading ? <span className="smf-spinner" /> : 'Analyse Fit →'}
              </button>
            </div>
          )}

          {/* ── RESULT ── */}
          {mode === 'result' && (result || apiNotice) && (
            <div className="smf-section smf-section--result">
              {apiNotice && <p className={`smf-notice ${result ? '' : 'smf-notice--success'}`}>{apiNotice}</p>}

              {!result ? (
                <div className="smf-result-actions">
                  <button className="smf-btn smf-btn--outline" onClick={() => setMode('choose')}>
                    Submit Another ↺
                  </button>
                  <button className="smf-btn smf-btn--primary" onClick={onClose}>
                    Done
                  </button>
                </div>
              ) : (
                <>

                  {/* Score ring */}
                  <div className="smf-score-row">
                    <div className="smf-score-ring" style={{ '--score': result.fitScore }}>
                      <span className="smf-score-ring__num">{result.fitScore}</span>
                      <span className="smf-score-ring__label">/ 100</span>
                    </div>
                    <div>
                      <p className="smf-score-badge">{result.fitLabel}</p>
                      <p className="smf-score-job">{job?.title} · {job?.company}</p>
                    </div>
                  </div>

                  <div className="smf-divider" />

                  {/* Strengths */}
                  <div className="smf-result-block">
                    <h3 className="smf-result-block__title smf-result-block__title--green">✔ Strengths</h3>
                    <ul className="smf-pill-list">
                      {result.strengths.map((s, i) => (
                        <li key={i} className="smf-pill smf-pill--green">{s}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Gaps */}
                  <div className="smf-result-block">
                    <h3 className="smf-result-block__title smf-result-block__title--amber">⚠ Gaps to Address</h3>
                    <ul className="smf-pill-list">
                      {result.gaps.map((g, i) => (
                        <li key={i} className="smf-pill smf-pill--amber">{g}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="smf-divider" />

                  {/* Learn / Practice / Share rows */}
                  <div className="smf-result-block">
                    <h3 className="smf-result-block__title">Learn · Practice · Share</h3>
                    <div className="smf-lps-table-wrap">
                      <table className="smf-lps-table" aria-label="Learn practice share plan">
                        <thead>
                          <tr>
                            <th>Track</th>
                            <th>Goal</th>
                            <th>Focus</th>
                            <th>Output</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.actionRows.map((row) => (
                            <tr key={row.track}>
                              <td><span className="smf-lps-track">{row.track}</span></td>
                              <td>{row.goal}</td>
                              <td>{row.focus}</td>
                              <td>{row.output}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="smf-divider" />

                  {/* Prep plan */}
                  <div className="smf-result-block">
                    <h3 className="smf-result-block__title">Weekly Preparation Plan</h3>
                    <div className="smf-weekly-table-wrap">
                      <table className="smf-weekly-table" aria-label="Weekly preparation plan">
                        <thead>
                          <tr>
                            <th>Week</th>
                            <th>Learn</th>
                            <th>Practice</th>
                            <th>Share</th>
                            <th>Outcome</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.weeklyPlan.map((row) => (
                            <tr key={row.week}>
                              <td><span className="smf-plan-item__week">{row.week}</span></td>
                              <td>{row.learn}</td>
                              <td>{row.practice}</td>
                              <td>{row.share}</td>
                              <td>{row.outcome}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="smf-export-row">
                    <button type="button" className="smf-btn smf-btn--outline smf-btn--small" onClick={exportToExcel}>
                      Export Excel
                    </button>
                    <button type="button" className="smf-btn smf-btn--outline smf-btn--small" onClick={exportToPdf}>
                      Export PDF
                    </button>
                    <button type="button" className="smf-btn smf-btn--outline smf-btn--small" onClick={exportToImage}>
                      Export Image
                    </button>
                  </div>

                  <div className="smf-result-actions">
                    <button className="smf-btn smf-btn--outline" onClick={() => setMode('choose')}>
                      Re-scan ↺
                    </button>
                    <button
                      className="smf-btn smf-btn--primary"
                      onClick={handleApplyNow}
                      disabled={!canApply || applyLoading}
                    >
                      {applyLoading ? <span className="smf-spinner" /> : 'Apply Now ↗'}
                    </button>
                  </div>
                  {applyNotice ? (
                    <p className={`smf-notice ${applyNotice.startsWith('Application submitted') ? 'smf-notice--success' : ''}`}>
                      {applyNotice}
                    </p>
                  ) : null}
                </>
              )}
            </div>
          )}

        </div>
      </aside>
    </>
  );
}

export default ScanMyFitDrawer;
